import { getProductDropdownLCA } from "../apis/Bonsai/productDropdownApi";
import i18n from "../i18n";

// Global cache for Bonsai dropdown data
class BonsaiCache {
  constructor() {
    this.cache = new Map(); // Changed to Map to support multiple languages
    this.isLoading = new Map(); // Track loading state per language
    this.loadingPromises = new Map(); // Track loading promises per language
    this.listeners = new Set();
  }

  // Get cached data or start loading if not cached
  async getData(language = null) {
    const targetLanguage = language || i18n.language;
    const cacheKey = targetLanguage || "default";

    // Return cached data if available
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // If already loading, return the existing promise
    if (this.isLoading.get(cacheKey) && this.loadingPromises.has(cacheKey)) {
      return this.loadingPromises.get(cacheKey);
    }

    // Start loading
    this.isLoading.set(cacheKey, true);
    const promise = this.loadData(targetLanguage);
    this.loadingPromises.set(cacheKey, promise);

    return promise;
  }

  // Load data from API
  async loadData(language = null) {
    const targetLanguage = language || i18n.language;
    const cacheKey = targetLanguage || "default";

    return new Promise((resolve, reject) => {
      getProductDropdownLCA({
        setDropdownData: (data) => {
          this.cache.set(cacheKey, data);
          this.isLoading.set(cacheKey, false);
          this.loadingPromises.delete(cacheKey);

          // Notify all listeners with language info
          this.listeners.forEach((callback) => callback(data, targetLanguage));

          resolve(data);
        },
        language: targetLanguage,
      });
    });
  }

  // Preload data (fire and forget)
  preload(language = null) {
    const targetLanguage = language || i18n.language;
    const cacheKey = targetLanguage || "default";

    if (!this.cache.has(cacheKey) && !this.isLoading.get(cacheKey)) {
      this.getData(targetLanguage).catch((error) => {
        console.error("Failed to preload Bonsai data:", error);
        this.isLoading.set(cacheKey, false);
        this.loadingPromises.delete(cacheKey);
      });
    }
  }

  // Subscribe to data updates
  subscribe(callback) {
    this.listeners.add(callback);

    // If data is already available for any language, call immediately
    if (this.cache.size > 0) {
      // Call with the first available data for backward compatibility
      const firstData = this.cache.values().next().value;
      callback(firstData);
    }

    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  // Clear cache (useful for testing or if data needs refresh)
  clear(language = null) {
    if (language) {
      const cacheKey = language;
      this.cache.delete(cacheKey);
      this.isLoading.delete(cacheKey);
      this.loadingPromises.delete(cacheKey);
    } else {
      this.cache.clear();
      this.isLoading.clear();
      this.loadingPromises.clear();
    }
  }

  // Check if data is available
  isDataAvailable(language = null) {
    if (language) {
      return this.cache.has(language);
    }
    return this.cache.size > 0;
  }

  // Check if currently loading
  isCurrentlyLoading(language = null) {
    if (language) {
      return this.isLoading.get(language) || false;
    }
    return Array.from(this.isLoading.values()).some((loading) => loading);
  }
}

// Export singleton instance
export const bonsaiCache = new BonsaiCache();
export default bonsaiCache;
